import { useState, useCallback } from 'react';
import {
  ConversionType,
  ConversionProgress,
  ConversionResult,
  ConversionError,
  convertFile,
  convertMultipleFiles,
  validateFile,
  downloadFile,
  cleanupDownloadUrl
} from '../services/convertService';

export type ConversionStatus = 'idle' | 'uploading' | 'converting' | 'downloading' | 'completed' | 'error';

interface UseFileConvertReturn {
  file: File | null;
  files: File[];
  status: ConversionStatus;
  progress: number;
  error: ConversionError | null;
  downloadUrl: string | null;
  fileName: string | null;
  compressedSize: number | null;
  setFile: (file: File | null) => void;
  setFiles: (files: File[]) => void;
  startConversion: (conversionType: ConversionType, allowedTypes?: string[]) => Promise<void>;
  startMultipleConversion: (conversionType: ConversionType) => Promise<void>;
  download: () => void;
  reset: () => void;
}

export function useFileConvert(): UseFileConvertReturn {
  const [file, setFile] = useState<File | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [status, setStatus] = useState<ConversionStatus>('idle');
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<ConversionError | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [compressedSize, setCompressedSize] = useState<number | null>(null);

  const handleProgress = useCallback((progressData: ConversionProgress) => {
    setStatus(progressData.stage);
    setProgress(progressData.percent);
  }, []);

  const startConversion = useCallback(async (
    conversionType: ConversionType,
    allowedTypes: string[] = ['pdf']
  ) => {
    if (!file) {
      setError({
        code: 'UNKNOWN',
        message: 'No file selected'
      });
      return;
    }

    // 验证文件
    const validationError = validateFile(file, allowedTypes);
    if (validationError) {
      setError(validationError);
      setStatus('error');
      return;
    }

    try {
      setError(null);
      setStatus('uploading');
      setProgress(0);

      const result: ConversionResult = await convertFile(
        file,
        conversionType,
        handleProgress
      );

      setDownloadUrl(result.downloadUrl);
      setFileName(result.fileName);
      setCompressedSize(result.compressedSize || null);
      setStatus('completed');
      setProgress(100);
    } catch (err) {
      setError(err as ConversionError);
      setStatus('error');
      setProgress(0);
    }
  }, [file, handleProgress]);

  const startMultipleConversion = useCallback(async (
    conversionType: ConversionType
  ) => {
    if (files.length === 0) {
      setError({
        code: 'UNKNOWN',
        message: 'No files selected'
      });
      return;
    }

    try {
      setError(null);
      setStatus('uploading');
      setProgress(0);

      const result: ConversionResult = await convertMultipleFiles(
        files,
        conversionType,
        handleProgress
      );

      setDownloadUrl(result.downloadUrl);
      setFileName(result.fileName);
      setCompressedSize(result.compressedSize || null);
      setStatus('completed');
      setProgress(100);
    } catch (err) {
      setError(err as ConversionError);
      setStatus('error');
      setProgress(0);
    }
  }, [files, handleProgress]);

  const download = useCallback(() => {
    if (downloadUrl && fileName) {
      downloadFile(downloadUrl, fileName);
    }
  }, [downloadUrl, fileName]);

  const reset = useCallback(() => {
    if (downloadUrl) {
      cleanupDownloadUrl(downloadUrl);
    }
    setFile(null);
    setFiles([]);
    setStatus('idle');
    setProgress(0);
    setError(null);
    setDownloadUrl(null);
    setFileName(null);
    setCompressedSize(null);
  }, [downloadUrl]);

  return {
    file,
    files,
    status,
    progress,
    error,
    downloadUrl,
    fileName,
    compressedSize,
    setFile,
    setFiles,
    startConversion,
    startMultipleConversion,
    download,
    reset
  };
}